import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Snackbar, Box } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { liveLinkStatusService, LiveLinkStatus } from '../services/liveLinkStatusService';
import { clientService } from '../services/clientService';
import { projectService } from '../services/projectService';
import { clientAssignmentService } from '../services/clientAssignmentService';
import { useAuth } from '../contexts/AuthContext';
import ImportLinksModal from '../components/LiveLinkStatus/ImportLinksModal';
import EditLinkStatusModal from '../components/LiveLinkStatus/EditLinkStatusModal';

const LiveLinkStatusPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, isSuperAdmin, hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [publisherFilter, setPublisherFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // '' = All months
  const [yearFilter, setYearFilter] = useState(''); // '' = All years
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<LiveLinkStatus | null>(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  
  // Check All job tracking state
  const [checkAllJob, setCheckAllJob] = useState<{
    jobId: string;
    status: 'running' | 'completed' | 'failed';
    progress: { checked: number; total: number };
    message: string;
  } | null>(null);

  const isPublisher = user?.role === 'PUBLISHER';
  const isAdmin = user?.role === 'ADMIN';
  const isAgency = user?.role === 'AGENCY';
  const isClient = user?.role === 'CLIENT';
  const canMonitor =
    isSuperAdmin || isAgency || isClient || isPublisher || hasPermission('monitoring');
  const canRunAdminActions = isSuperAdmin || (isAdmin && hasPermission('monitoring'));
  const canImportUrls = hasPermission('monitoring.import_urls');
  const canCheckLinks = canMonitor;

  // Fetch clients for filter (backend scopes agency to its own clients)
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: isSuperAdmin || isAgency, // All clients for Super Admin, own clients for Agency
  });

  // Fetch assigned clients for Admin users
  const { data: assignedClientsData } = useQuery({
    queryKey: ['assigned-clients', user?.id],
    queryFn: () => clientAssignmentService.getAssignedClients(user!.id),
    enabled: isAdmin && canMonitor && !!user?.id,
  });

  // Use assigned clients for Admin, all (scoped) clients for Super Admin/Agency
  const clients = isAdmin 
    ? (assignedClientsData?.data || [])
    : (clientsData?.data || []);

  const { data: projectsData } = useQuery({
    queryKey: ['projects', clientFilter, user?.clientId, user?.role],
    queryFn: () =>
      projectService.getProjects({
        limit: 1000,
        clientId: isClient ? user?.clientId : clientFilter || undefined,
      }),
    enabled:
      !isPublisher &&
      (isClient ? !!user?.clientId : canMonitor),
  });

  const projects = projectsData?.data || [];

  // Fetch link statuses
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['live-link-statuses', statusFilter, typeFilter, publisherFilter, clientFilter, projectFilter, monthFilter, yearFilter, searchTerm, page, rowsPerPage],
    queryFn: () => liveLinkStatusService.getLiveLinkStatuses({
      status: statusFilter || undefined,
      type: typeFilter || undefined,
      publisherId: publisherFilter || undefined,
      clientId: clientFilter || undefined,
      projectId: projectFilter || undefined,
      month: monthFilter ? parseInt(monthFilter) : undefined,
      year: yearFilter ? parseInt(yearFilter) : undefined,
      search: searchTerm || undefined,
      page: page + 1,
      limit: rowsPerPage,
    }),
    enabled: canMonitor,
  });

  // Fetch stats
  const { data: statsData, refetch: refetchStats } = useQuery({
    queryKey: ['live-link-stats', user?.id, user?.role],
    queryFn: () => liveLinkStatusService.getStats(),
    staleTime: 0,
    gcTime: 0,
    enabled: canMonitor,
  });

  // Check single link mutation
  const checkLinkMutation = useMutation({
    mutationFn: (id: string) => liveLinkStatusService.checkLink(id),
    onSuccess: () => {
      // Only invalidate the current page queries, not trigger new checks
      queryClient.invalidateQueries({ queryKey: ['live-link-statuses'] });
      queryClient.invalidateQueries({ queryKey: ['live-link-stats'] });
    },
    onError: (error) => {
      console.error('[LiveLinkStatus] Check link error:', error);
      alert('Failed to check link. Please try again.');
    },
  });

  // Delete link mutation
  const deleteLinkMutation = useMutation({
    mutationFn: (id: string) => liveLinkStatusService.deleteLinkStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['live-link-statuses'] });
      queryClient.invalidateQueries({ queryKey: ['live-link-stats'] });
    },
  });

  // Check all links mutation
  const checkAllMutation = useMutation({
    mutationFn: () => liveLinkStatusService.checkAllLinks(),
    onSuccess: (response) => {
      if (response.jobId) {
        // Start background job tracking
        setCheckAllJob({
          jobId: response.jobId,
          status: 'running',
          progress: { checked: 0, total: response.totalLinks },
          message: `Starting to check ${response.totalLinks} links...`
        });
        setSnackbar({ 
          open: true, 
          message: `Started checking ${response.totalLinks} links in background. You can navigate to other pages.`, 
          severity: 'info' 
        });
        // Start polling for job status
        startJobStatusPolling(response.jobId);
      } else {
        setSnackbar({ open: true, message: response.message, severity: 'success' });
      }
    },
    onError: (error) => {
      console.error('[LiveLinkStatus] Check all error:', error);
      setSnackbar({ open: true, message: 'Failed to start checking links', severity: 'error' });
    },
  });

  // Job status polling
  const startJobStatusPolling = (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await liveLinkStatusService.getJobStatus(jobId);
        const jobData = statusResponse.data;
        
        setCheckAllJob({
          jobId: jobData.id,
          status: jobData.status,
          progress: jobData.progress,
          message: jobData.message
        });
        
        if (jobData.status === 'completed') {
          clearInterval(pollInterval);
          setSnackbar({ 
            open: true, 
            message: `✅ Successfully checked all ${jobData.progress.total} links!`, 
            severity: 'success' 
          });
          // Refresh data to show updated results
          setTimeout(() => {
            refetch();
            refetchStats();
            setCheckAllJob(null); // Clear job status
          }, 3000);
        } else if (jobData.status === 'failed') {
          clearInterval(pollInterval);
          setSnackbar({ 
            open: true, 
            message: `❌ Failed to check links: ${jobData.error || 'Unknown error'}`, 
            severity: 'error' 
          });
          setCheckAllJob(null);
        }
      } catch (error) {
        console.error('Job status polling error:', error);
        clearInterval(pollInterval);
        setCheckAllJob(null);
      }
    }, 2000); // Poll every 2 seconds
  };

  const linkStatuses = data?.data?.linkStatuses || [];
  const pagination = data?.data?.pagination;
  const stats = statsData?.data;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEdit = (link: LiveLinkStatus) => {
    setSelectedLink(link);
    setEditModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this link status?')) {
      await deleteLinkMutation.mutateAsync(id);
    }
  };

  const renderStatusPill = (status: string) => {
    const statusConfig: Record<string, { color: string; bg: string; icon: string }> = {
      'OK_200': { color: '#10b981', bg: '#d1fae5', icon: '✅' },
      'ERROR_4XX': { color: '#ef4444', bg: '#fee2e2', icon: '❌' },
      'ERROR_5XX': { color: '#ef4444', bg: '#fee2e2', icon: '❌' },
      'TIMEOUT': { color: '#f59e0b', bg: '#fef3c7', icon: '⏱️' },
      'PENDING': { color: '#6b7280', bg: '#f3f4f6', icon: '⏳' },
      'CHECKING': { color: '#3b82f6', bg: '#dbeafe', icon: '🔄' },
      'UNKNOWN': { color: '#7c3aed', bg: '#ede9fe', icon: '❓' },
    };
    
    const config = statusConfig[status] || { color: '#6b7280', bg: '#f3f4f6', icon: '❓' };
    
    return (
      <span 
        className="status-pill"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`
        }}
      >
        <span className="status-icon">{config.icon}</span>
        {liveLinkStatusService.getStatusLabel(status as any)}
      </span>
    );
  };

  if (!canMonitor) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box className="page">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pbarAnim { 0% { width: 0%; } 60% { width: 72%; } 80% { width: 78%; } 100% { width: 82%; } }
        
        .page { padding: 22px; display: flex; flex-direction: column; gap: 16px; }
        
        .ph { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .ph-title { font-size: 19px; font-weight: 700; letter-spacing: -0.4px; color: #111827; }
        .ph-actions { display: flex; align-items: center; gap: 8px; }
        
        .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 6px; font-family: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; border: none; transition: all 0.14s; white-space: nowrap; line-height: 1; }
        .btn svg { width: 13px; height: 13px; flex-shrink: 0; }
        .btn-primary { background: #4F46E5; color: #fff; box-shadow: 0 1px 2px rgba(79,70,229,0.25); }
        .btn-primary:hover:not(:disabled) { background: #4338CA; transform: translateY(-1px); box-shadow: 0 3px 8px rgba(79,70,229,0.3); }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-outline { background: #FFFFFF; color: #4F46E5; border: 1.5px solid #C7D2FE; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .btn-outline:hover { background: #EEF2FF; border-color: #A5B4FC; }
        .btn-secondary { background: #FFFFFF; color: #374151; border: 1px solid #E5E7EB; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .btn-secondary:hover { background: #F9FAFB; color: #111827; }
        
        .check-banner { background: linear-gradient(135deg, #2563EB 0%, #06B6D4 100%); border-radius: 8px; padding: 18px 22px; color: #fff; box-shadow: 0 4px 16px rgba(37,99,235,0.25); animation: fadeUp 0.25s ease forwards; }
        .check-banner-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .check-spinner { width: 24px; height: 24px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.8s linear infinite; flex-shrink: 0; }
        .check-title { font-size: 14.5px; font-weight: 600; letter-spacing: -0.15px; }
        .check-sub { font-size: 12.5px; opacity: 0.82; margin-top: 1px; }
        .check-pbar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .check-pbar-wrap { background: rgba(255,255,255,0.22); border-radius: 99px; height: 6px; position: relative; overflow: hidden; flex: 1; }
        .check-pbar-fill { height: 100%; background: #fff; border-radius: 99px; transition: width 0.3s ease; }
        .check-count { font-size: 12px; font-weight: 600; opacity: 0.9; flex-shrink: 0; }
        .check-note { display: flex; align-items: center; gap: 6px; font-size: 12px; opacity: 0.8; }
        .check-note svg { width: 13px; height: 13px; flex-shrink: 0; }
        
        .stats-row { display: grid; grid-template-columns: 1.15fr 1fr 1fr 1fr 1fr; gap: 11px; }
        .stat-card { border-radius: 8px; padding: 16px 18px; display: flex; flex-direction: column; gap: 6px; transition: transform 0.18s, box-shadow 0.18s; cursor: default; }
        .stat-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04); }
        .stat-neutral { background: #FFFFFF; border: 1px solid #E5E7EB; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .stat-neutral .stat-val { color: #111827; }
        .stat-neutral .stat-lbl { color: #6B7280; }
        .stat-green { background: linear-gradient(135deg, #15803D, #16A34A); }
        .stat-red { background: linear-gradient(135deg, #B91C1C, #DC2626); }
        .stat-orange { background: linear-gradient(135deg, #C2410C, #EA580C); }
        .stat-grey { background: linear-gradient(135deg, #52525B, #71717A); }
        .stat-val { font-size: 26px; font-weight: 700; letter-spacing: -0.6px; line-height: 1; font-family: 'JetBrains Mono', monospace; }
        .stat-lbl { font-size: 12px; font-weight: 500; }
        .stat-card.stat-green .stat-val, .stat-card.stat-green .stat-lbl,
        .stat-card.stat-red .stat-val, .stat-card.stat-red .stat-lbl,
        .stat-card.stat-orange .stat-val, .stat-card.stat-orange .stat-lbl,
        .stat-card.stat-grey .stat-val, .stat-card.stat-grey .stat-lbl { color: #fff; }
        .stat-card.stat-green .stat-lbl, .stat-card.stat-red .stat-lbl,
        .stat-card.stat-orange .stat-lbl, .stat-card.stat-grey .stat-lbl { opacity: 0.82; }
        
        .filter-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); padding: 13px 16px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .f-search { display: flex; align-items: center; gap: 8px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; padding: 7px 11px; min-width: 240px; transition: all 0.15s; }
        .f-search:focus-within { background: #FFFFFF; border-color: #4F46E5; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        .f-search svg { width: 14px; height: 14px; color: #9CA3AF; flex-shrink: 0; }
        .f-search input { border: none; background: none; outline: none; font-family: inherit; font-size: 13px; color: #111827; width: 100%; }
        .f-search input::placeholder { color: #9CA3AF; }
        .fsel { appearance: none; border: 1px solid #E5E7EB; border-radius: 6px; padding: 7px 28px 7px 11px; font-family: inherit; font-size: 13px; color: #374151; background: #F9FAFB; outline: none; cursor: pointer; transition: all 0.14s; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 9px center; }
        .fsel:focus { border-color: #4F46E5; background-color: #FFFFFF; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        
        .tbl-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); overflow: hidden; animation: fadeUp 0.22s ease forwards; }
        .tbl-wrap { overflow-x: auto; }
        .links-tbl { width: 100%; border-collapse: collapse; min-width: 900px; }
        .links-tbl thead th { text-align: left; font-size: 10.5px; font-weight: 600; color: #9CA3AF; letter-spacing: 0.5px; text-transform: uppercase; padding: 12px 14px; border-bottom: 1px solid #E5E7EB; background: #FAFAFA; white-space: nowrap; user-select: none; }
        .links-tbl thead th:first-child { padding-left: 16px; }
        .links-tbl thead th:last-child { padding-right: 16px; }
        .links-tbl tbody tr { transition: background 0.08s; }
        .links-tbl tbody tr:hover { background: #F9FAFB; }
        .links-tbl tbody td { padding: 12px 14px; font-size: 13px; border-bottom: 1px solid #F3F4F6; vertical-align: middle; }
        .links-tbl tbody tr:last-child td { border-bottom: none; }
        .links-tbl tbody td:first-child { padding-left: 16px; }
        .links-tbl tbody td:last-child { padding-right: 16px; }
        
        .status-pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 11px; border-radius: 99px; font-size: 11.5px; font-weight: 600; white-space: nowrap; letter-spacing: 0.1px; }
        .status-icon { font-size: 13px; line-height: 1; }
        
        .type-tag { display: inline-block; padding: 4px 10px; border-radius: 99px; font-size: 11.5px; font-weight: 500; border: 1px solid #E5E7EB; background: #F9FAFB; color: #374151; }
        .type-placement { background: #EFF6FF; border-color: #BFDBFE; color: #1E40AF; }
        .type-external { background: #F0FDF4; border-color: #BBF7D0; color: #15803D; }
        
        .url-link { color: #4F46E5; font-size: 13px; font-weight: 500; text-decoration: none; display: block; max-width: 350px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .url-link:hover { text-decoration: underline; color: #4338CA; }
        
        .act-btns { display: inline-flex; gap: 4px; align-items: center; justify-content: flex-end; }
        .act-btn { width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: none; transition: all 0.14s; }
        .act-btn svg { width: 14px; height: 14px; color: #9CA3AF; }
        .act-btn.act-refresh:hover:not(:disabled) { background: #EEF2FF; }
        .act-btn.act-refresh:hover:not(:disabled) svg { color: #4F46E5; }
        .act-btn.act-open:hover { background: #F0FDFA; }
        .act-btn.act-open:hover svg { color: #0D9488; }
        .act-btn.act-edit:hover { background: #FFFBEB; }
        .act-btn.act-edit:hover svg { color: #D97706; }
        .act-btn.act-delete:hover { background: #FEF2F2; }
        .act-btn.act-delete:hover svg { color: #DC2626; }
        .act-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        
        .pag-bar { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-top: 1px solid #E5E7EB; background: #FAFAFA; }
        .pag-info { font-size: 12.5px; color: #6B7280; font-weight: 500; }
        .pag-controls { display: flex; align-items: center; gap: 12px; }
        .pag-sel { border: 1px solid #E5E7EB; background: #FFFFFF; border-radius: 6px; padding: 6px 10px; font-family: inherit; font-size: 12.5px; outline: none; cursor: pointer; color: #374151; transition: all 0.14s; }
        .pag-sel:focus { border-color: #4F46E5; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        .pag-btns { display: flex; align-items: center; gap: 4px; }
        .pag-btn { width: 32px; height: 32px; border-radius: 6px; border: 1px solid #E5E7EB; background: #FFFFFF; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.14s; }
        .pag-btn:hover:not(:disabled) { background: #F9FAFB; border-color: #D1D5DB; }
        .pag-btn svg { width: 14px; height: 14px; color: #374151; }
        .pag-btn:disabled { opacity: 0.4; cursor: not-allowed; background: #F9FAFB; }
        .pag-num { font-size: 12.5px; color: #374151; font-weight: 500; padding: 0 8px; }
        
        .empty-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; gap: 12px; text-align: center; }
        .empty-ico { width: 60px; height: 60px; background: #F9FAFB; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 4px; border: 1px solid #E5E7EB; }
        .empty-ico svg { width: 26px; height: 26px; color: #D1D5DB; }
        .empty-title { font-size: 15px; font-weight: 600; color: #111827; letter-spacing: -0.2px; }
        .empty-sub { font-size: 13px; color: #6B7280; max-width: 300px; line-height: 1.65; }
      `}</style>

      {/* Page header */}
      <div className="ph">
        <h1 className="ph-title">Live Link Monitoring</h1>
        <div className="ph-actions">
          {canImportUrls && (
            <button className="btn btn-outline" onClick={() => setImportModalOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"/>
                <line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
              Import URLs
            </button>
          )}
          {canRunAdminActions && (
            <>
              <button 
                className="btn btn-primary" 
                onClick={() => checkAllMutation.mutate()}
                disabled={checkAllMutation.isPending || checkAllJob?.status === 'running'}
              >
                {checkAllMutation.isPending || checkAllJob?.status === 'running' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}>
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                )}
                {checkAllMutation.isPending ? 'Starting...' : checkAllJob?.status === 'running' ? 'Checking...' : 'Check All'}
              </button>
              <button className="btn btn-secondary" onClick={() => refetch()}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Background check banner */}
      {checkAllJob && checkAllJob.status === 'running' && (
        <div className="check-banner">
          <div className="check-banner-top">
            <div className="check-spinner"></div>
            <div>
              <div className="check-title">Checking Links in Background</div>
              <div className="check-sub">{checkAllJob.message}</div>
            </div>
          </div>
          <div className="check-pbar-row">
            <div className="check-pbar-wrap">
              <div 
                className="check-pbar-fill" 
                style={{ width: `${(checkAllJob.progress.checked / checkAllJob.progress.total) * 100}%` }}
              ></div>
            </div>
            <span className="check-count">
              {checkAllJob.progress.checked}/{checkAllJob.progress.total}
            </span>
          </div>
          <div className="check-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            You can navigate to other pages while checking continues in the background.
          </div>
        </div>
      )}

      {/* Stats strip */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card stat-neutral">
            <div className="stat-lbl">Total Links</div>
            <div className="stat-val">{stats.total}</div>
          </div>
          <div className="stat-card stat-green">
            <div className="stat-lbl">OK (200)</div>
            <div className="stat-val">{stats.ok}</div>
          </div>
          <div className="stat-card stat-red">
            <div className="stat-lbl">Errors</div>
            <div className="stat-val">{stats.errorTotal}</div>
          </div>
          <div className="stat-card stat-orange">
            <div className="stat-lbl">Timeout</div>
            <div className="stat-val">{stats.timeout}</div>
          </div>
          <div className="stat-card stat-grey">
            <div className="stat-lbl">Pending</div>
            <div className="stat-val">{stats.pending}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-card">
        <div className="f-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input 
            type="text" 
            placeholder="Search by URL..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="fsel" 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ minWidth: '150px' }}
        >
          <option value="">Status</option>
          <option value="OK_200">OK (200)</option>
          <option value="ERROR_4XX">4xx Error</option>
          <option value="ERROR_5XX">5xx Error</option>
          <option value="OTHER">Other Errors</option>
        </select>
        <select 
          className="fsel" 
          value={typeFilter} 
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ minWidth: '130px' }}
        >
          <option value="">Type</option>
          <option value="placement">Placement</option>
          <option value="external">External</option>
        </select>
        {!isPublisher && !isClient && (
          <select 
            className="fsel" 
            value={clientFilter} 
            onChange={(e) => {
              setClientFilter(e.target.value);
              setProjectFilter('');
            }}
            style={{ minWidth: '150px' }}
          >
            <option value="">Client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        )}
        {!isPublisher && (
          <select
            className="fsel"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            style={{ minWidth: '150px' }}
          >
            <option value="">Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.projectName}
              </option>
            ))}
          </select>
        )}
        <select 
          className="fsel" 
          value={monthFilter} 
          onChange={(e) => setMonthFilter(e.target.value)}
          style={{ minWidth: '130px' }}
        >
          <option value="">All Months</option>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(2000, i).toLocaleString('default', { month: 'long' })}
            </option>
          ))}
        </select>
        <select 
          className="fsel" 
          value={yearFilter} 
          onChange={(e) => setYearFilter(e.target.value)}
        >
          <option value="">All Years</option>
          {Array.from({ length: 5 }, (_, i) => {
            const year = new Date().getFullYear() - i;
            return <option key={year} value={year}>{year}</option>;
          })}
        </select>
      </div>

      {/* Main table */}
      <div className="tbl-card">
        <div className="tbl-wrap">
          <table className="links-tbl">
            <thead>
              <tr>
                <th>Status</th>
                <th>Live URL</th>
                {!isPublisher && !isClient && <th>Publisher</th>}
                <th>Type</th>
                <th>Last Checked</th>
                <th>Next Check</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {checkAllMutation.isPending ? (
                <tr>
                  <td colSpan={isPublisher || isClient ? 6 : 7} style={{ textAlign: 'center', padding: '3rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                      <div className="check-spinner" style={{ width: '60px', height: '60px' }}></div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#2563eb' }}>
                        Checking All Links...
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Populating missing orders and checking all URLs
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        This may take a few moments. Please wait...
                      </div>
                    </div>
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={isPublisher || isClient ? 6 : 7} style={{ textAlign: 'center', padding: '2rem' }}>
                    <div className="check-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                  </td>
                </tr>
              ) : linkStatuses.length === 0 ? (
                <tr>
                  <td colSpan={isPublisher || isClient ? 6 : 7} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No link statuses found
                  </td>
                </tr>
              ) : (
                linkStatuses.map((link) => (
                  <tr key={link.id}>
                    <td>{renderStatusPill(link.status)}</td>
                    <td>
                      <a 
                        href={link.liveUrl} 
                        target="_blank" 
                        rel="noopener noreferrer nofollow"
                        className="url-link"
                        title={link.liveUrl}
                      >
                        {link.liveUrl}
                      </a>
                    </td>
                    {!isPublisher && !isClient && (
                      <td>
                        {link.publisher ? (
                          <>
                            <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {link.publisher.publisherName}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {link.publisher.email}
                            </div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                    )}
                    <td>
                      <span className={`type-tag ${link.isExternal ? 'type-external' : 'type-placement'}`}>
                        {link.isExternal ? 'External' : 'Placement'}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {liveLinkStatusService.getTimeAgo(link.lastCheckedAt)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {liveLinkStatusService.formatDate(link.lastCheckedAt)}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      {liveLinkStatusService.formatDate(link.nextCheckAt)}
                    </td>
                    <td>
                      <div className="act-btns">
                        <button 
                          className="act-btn act-refresh" 
                          onClick={() => checkLinkMutation.mutate(link.id)}
                          disabled={checkLinkMutation.isPending || !canCheckLinks}
                          title="Check Now"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"/>
                            <polyline points="1 20 1 14 7 14"/>
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                          </svg>
                        </button>
                        <button 
                          className="act-btn act-open" 
                          onClick={() => {
                            const newWindow = window.open();
                            if (newWindow) {
                              newWindow.opener = null;
                              newWindow.location = link.liveUrl;
                            }
                          }}
                          title="Open URL"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                            <polyline points="15 3 21 3 21 9"/>
                            <line x1="10" y1="14" x2="21" y2="3"/>
                          </svg>
                        </button>
                        {isSuperAdmin && (
                          <>
                            <button 
                              className="act-btn act-edit" 
                              onClick={() => handleEdit(link)}
                              title="Edit"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button 
                              className="act-btn act-delete" 
                              onClick={() => handleDelete(link.id)}
                              disabled={deleteLinkMutation.isPending}
                              title="Delete"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination && (
          <div className="pag-bar">
            <div className="pag-info">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, pagination.total)} of {pagination.total} entries
            </div>
            <div className="pag-controls">
              <select 
                className="pag-sel" 
                value={rowsPerPage} 
                onChange={handleChangeRowsPerPage}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <div className="pag-btns">
                <button 
                  className="pag-btn" 
                  onClick={(e) => handleChangePage(e, 0)}
                  disabled={page === 0}
                  title="First page"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="11 17 6 12 11 7"/>
                    <polyline points="18 17 13 12 18 7"/>
                  </svg>
                </button>
                <button 
                  className="pag-btn" 
                  onClick={(e) => handleChangePage(e, page - 1)}
                  disabled={page === 0}
                  title="Previous page"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <span className="pag-num">Page {page + 1} of {Math.ceil(pagination.total / rowsPerPage)}</span>
                <button 
                  className="pag-btn" 
                  onClick={(e) => handleChangePage(e, page + 1)}
                  disabled={page >= Math.ceil(pagination.total / rowsPerPage) - 1}
                  title="Next page"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
                <button 
                  className="pag-btn" 
                  onClick={(e) => handleChangePage(e, Math.ceil(pagination.total / rowsPerPage) - 1)}
                  disabled={page >= Math.ceil(pagination.total / rowsPerPage) - 1}
                  title="Last page"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="13 17 18 12 13 7"/>
                    <polyline points="6 17 11 12 6 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ImportLinksModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => {
          setImportModalOpen(false);
          refetch();
        }}
      />

      <EditLinkStatusModal
        open={editModalOpen}
        linkStatus={selectedLink}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedLink(null);
        }}
        onSuccess={() => {
          setEditModalOpen(false);
          setSelectedLink(null);
          refetch();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LiveLinkStatusPage;
